import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, DateInput } from '@safetyhub/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { useModalStore } from '../../stores/modal';
import { extractApiError } from '../../lib/api';
import { submitActionResult, uploadResolutionPhoto } from '../../lib/inspections';
import Modal from '../Modal';
import { useToastStore } from '../../stores/toast';
import { ActionItemsInput, notesToItems, itemsToNotes } from '../inspection/shared/ActionItemsInput';
import { formatDateISO, parseDateLocalToUTC, toISOString } from '@safetyhub/shared';

const MAX_PHOTOS = 5;

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'action-result-form';

interface PhotoPreview {
  file?: File;
  previewUrl: string;
  /** 이미 서버에 업로드된 사진 (기존 사진 유지용) */
  uploaded?: { url: string; thumbnailUrl?: string; fileId?: string };
}

interface StateData {
  photos: PhotoPreview[];
  representativeIndex: number;
  notes: string;
  actionItems: string[];
}

interface ActionResultModalProps {
  /** 성공 후 호출되는 콜백 */
  onSuccess?: () => void;
}

/**
 * 이전 발견사항 조치결과 입력 모달
 *
 * - 미해결/해결됨 상태 선택
 * - 사진 첨부 (최대 5장, 선택) — 상태별 분리
 * - 조치 사항(해결됨) / 메모(미해결) 입력
 */
export default function ActionResultModal({ onSuccess }: ActionResultModalProps) {
  const queryClient = useQueryClient();
  const { currentMembership } = useAuthStore();
  const { actionResult, closeActionResult } = useModalStore();
  const companyId = currentMembership?.companyId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOpen = !!actionResult;
  const inspectionId = actionResult?.inspectionId;
  const findingId = actionResult?.findingId;

  // 폼 상태
  const [isResolved, setIsResolved] = useState<boolean>(false);
  const [resolvedDate, setResolvedDate] = useState<string>(formatDateISO(new Date()));
  const emptyState: StateData = { photos: [], representativeIndex: 0, notes: '', actionItems: [] };
  // 해결/미해결 각각 별도 데이터 관리 (사진, 메모, 조치사항)
  const [resolvedData, setResolvedData] = useState<StateData>({ ...emptyState });
  const [unresolvedData, setUnresolvedData] = useState<StateData>({ ...emptyState });

  // 현재 상태에 해당하는 데이터
  const currentData = isResolved ? resolvedData : unresolvedData;
  const setCurrentData = isResolved ? setResolvedData : setUnresolvedData;

  // UI 상태
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToastStore();

  // 모달 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen && actionResult) {
      const wasResolved = actionResult.existingIsResolved || false;
      setIsResolved(wasResolved);

      // 해결 날짜 프리필: 기존 resolvedAt이 있으면 사용, 없으면 min(오늘, maxDate)
      if (wasResolved && actionResult.existingResolvedAt) {
        setResolvedDate(formatDateISO(new Date(actionResult.existingResolvedAt)));
      } else {
        const todayStr = formatDateISO(new Date());
        if (actionResult.nextInspectionDate) {
          // KST 날짜 문자열로 먼저 변환 후 하루 빼기 (UTC 기준 연산 버그 방지)
          const nextDateKST = formatDateISO(new Date(actionResult.nextInspectionDate));
          const [y, m, d] = nextDateKST.split('-').map(Number);
          const prevDay = new Date(y, m - 1, d - 1);
          const maxStr = `${prevDay.getFullYear()}-${String(prevDay.getMonth() + 1).padStart(2, '0')}-${String(prevDay.getDate()).padStart(2, '0')}`;
          setResolvedDate(todayStr <= maxStr ? todayStr : maxStr);
        } else {
          setResolvedDate(todayStr);
        }
      }

      // 해결됨/미해결 텍스트 데이터
      const existingNotes = actionResult.existingNotes || '';

      // 기존 사진 프리필
      const existingPhotos: PhotoPreview[] = [];
      if (actionResult.existingResolutionPhotos && actionResult.existingResolutionPhotos.length > 0) {
        actionResult.existingResolutionPhotos.forEach((p: { url: string; thumbnailUrl?: string; fileId?: string }) => {
          existingPhotos.push({
            previewUrl: p.thumbnailUrl || p.url,
            uploaded: p,
          });
        });
      } else if (actionResult.existingResolutionPhotoUrl) {
        existingPhotos.push({
          previewUrl: actionResult.existingResolutionPhotoUrl,
          uploaded: { url: actionResult.existingResolutionPhotoUrl },
        });
      }

      // 대표 사진 인덱스 복원
      const savedRepUrl = actionResult.existingResolutionPhotoUrl;
      const repIdx = (() => {
        if (savedRepUrl && existingPhotos.length > 1) {
          const idx = existingPhotos.findIndex(p => p.uploaded?.url === savedRepUrl);
          return idx >= 0 ? idx : 0;
        }
        return 0;
      })();

      // 기존 상태에 해당하는 슬롯에만 프리필
      if (wasResolved) {
        setResolvedData({ photos: existingPhotos, representativeIndex: repIdx, notes: '', actionItems: notesToItems(existingNotes) });
        setUnresolvedData({ ...emptyState });
      } else {
        setUnresolvedData({ photos: existingPhotos, representativeIndex: repIdx, notes: existingNotes, actionItems: [] });
        // 새 입력 + measures 있으면 해결됨 슬롯에 프리필
        const prefillItems = !existingNotes && actionResult.measures?.length
          ? actionResult.measures
          : [];
        setResolvedData({ ...emptyState, actionItems: prefillItems });
      }
    }
  }, [isOpen, actionResult]);

  // 미해결→해결됨 전환 시 measures 프리필
  useEffect(() => {
    if (!isOpen || !isResolved) return;
    // 해결됨 슬롯에 actionItems가 비어있고, measures가 있으면 프리필
    if (resolvedData.actionItems.length === 0 && actionResult?.measures?.length) {
      setResolvedData(prev => ({ ...prev, actionItems: actionResult.measures! }));
    }
  }, [isResolved]);

  // 컴포넌트 언마운트 시 미리보기 URL 정리
  useEffect(() => {
    return () => {
      [...resolvedData.photos, ...unresolvedData.photos].forEach(p => {
        if (p.file) URL.revokeObjectURL(p.previewUrl);
      });
    };
  }, []);

  const handleClose = () => {
    if (isLoading) return;
    [...resolvedData.photos, ...unresolvedData.photos].forEach(p => {
      if (p.file) URL.revokeObjectURL(p.previewUrl);
    });
    setResolvedData({ ...emptyState });
    setUnresolvedData({ ...emptyState });
    setIsResolved(false);
    setResolvedDate(formatDateISO(new Date()));
    closeActionResult();
  };

  const validateFile = useCallback((file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      showToast('이미지 파일만 업로드할 수 있습니다.', 'error');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('파일 크기가 10MB를 초과합니다.', 'error');
      return false;
    }
    return true;
  }, [showToast]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remaining = MAX_PHOTOS - currentData.photos.length;
    if (remaining <= 0) {
      showToast(`최대 ${MAX_PHOTOS}장까지 업로드할 수 있습니다.`, 'error');
      return;
    }
    const validFiles = fileArray.filter(validateFile).slice(0, remaining);
    const newPhotos: PhotoPreview[] = validFiles.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setCurrentData(prev => ({ ...prev, photos: [...prev.photos, ...newPhotos] }));
  }, [currentData.photos.length, validateFile, showToast, setCurrentData]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    setCurrentData(prev => {
      const removed = prev.photos[index];
      if (removed.file) URL.revokeObjectURL(removed.previewUrl);
      const newPhotos = prev.photos.filter((_, i) => i !== index);
      let newRepIdx = prev.representativeIndex;
      if (index === newRepIdx) newRepIdx = 0;
      else if (index < newRepIdx) newRepIdx -= 1;
      return { ...prev, photos: newPhotos, representativeIndex: newRepIdx };
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !inspectionId || !findingId) return;

    // 해결됨: 항목 기반 검증, 미해결: 텍스트 기반
    const effectiveNotes = isResolved ? itemsToNotes(currentData.actionItems) : currentData.notes;
    if (isResolved && currentData.actionItems.length === 0) {
      showToast('해결됨 상태에서는 조치 사항을 입력해야 합니다.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // 다중 사진 업로드
      const uploadedPhotos: Array<{ url: string; thumbnailUrl?: string; fileId?: string }> = [];

      for (const photo of currentData.photos) {
        if (photo.uploaded) {
          uploadedPhotos.push(photo.uploaded);
        } else if (photo.file) {
          const uploadResponse = await uploadResolutionPhoto(photo.file, companyId);
          if (!uploadResponse.success || !uploadResponse.data) {
            throw new Error(uploadResponse.error?.message || '사진 업로드에 실패했습니다.');
          }
          uploadedPhotos.push({
            url: uploadResponse.data.url,
            fileId: uploadResponse.data.id,
          });
        }
      }

      // 대표 사진을 배열 맨 앞으로 정렬
      const repIdx = currentData.representativeIndex < uploadedPhotos.length ? currentData.representativeIndex : 0;
      if (repIdx > 0 && uploadedPhotos.length > 1) {
        const [rep] = uploadedPhotos.splice(repIdx, 1);
        uploadedPhotos.unshift(rep);
      }
      const representativePhoto = uploadedPhotos[0];

      // 조치결과 제출
      const response = await submitActionResult(
        inspectionId,
        findingId,
        {
          isResolved,
          notes: effectiveNotes || null,
          resolutionPhotoUrl: representativePhoto?.url ?? null,
          resolutionPhotoId: representativePhoto?.fileId ?? null,
          resolutionPhotos: uploadedPhotos.length > 0 ? uploadedPhotos : null,
          resolvedAt: isResolved ? toISOString(parseDateLocalToUTC(resolvedDate)) : null,
        },
        companyId
      );

      if (!response.success) {
        throw new Error(response.error?.message || '조치결과 저장에 실패했습니다.');
      }

      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', inspectionId] });

      showToast(
        isResolved ? '발견사항이 해결 처리되었습니다.' : '조치결과가 저장되었습니다.',
        'success'
      );
      onSuccess?.();
      handleClose();
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 날짜 선택 범위 계산
  const resolvedDateMin = actionResult?.inspectionCreatedAt
    ? formatDateISO(new Date(actionResult.inspectionCreatedAt))
    : undefined;
  const resolvedDateMax = (() => {
    if (actionResult?.nextInspectionDate) {
      // 다음 점검일을 KST 날짜 문자열로 먼저 변환 후 하루 빼기 (UTC 기준 연산 버그 방지)
      const nextDateKST = formatDateISO(new Date(actionResult.nextInspectionDate));
      const [y, m, d] = nextDateKST.split('-').map(Number);
      const prevDay = new Date(y, m - 1, d - 1);
      return `${prevDay.getFullYear()}-${String(prevDay.getMonth() + 1).padStart(2, '0')}-${String(prevDay.getDate()).padStart(2, '0')}`;
    }
    return formatDateISO(new Date());
  })();

  const photos = currentData.photos;
  const representativeIndex = currentData.representativeIndex;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="조치 결과 입력"
      description="이전 점검의 발견사항에 대한 조치 결과를 입력하세요."
      size="lg"
      preventClose={isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          {/* '해결 완료'는 확정 어포던스라 초록(success) 유지 — 브랜드로 옮기지 않는다. */}
          {/* 푸터는 base가 본문과 **형제**로 렌더하므로 아래 `<form>` 밖이다 — `form` 속성으로
              이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다). */}
          <Button
            variant={isResolved ? 'success' : 'primary'}
            type="submit"
            form={FORM_ID}
            disabled={isLoading}
          >
            {isLoading ? '저장 중...' : isResolved ? '해결 완료' : '저장'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* 해결 상태 선택 */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-3">
              현재 상태
            </label>
            <div className="flex gap-4">
              <label
                className={`flex-1 cursor-pointer rounded-card border-2 p-4 transition-colors ${
                  !isResolved
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                <input
                  type="radio"
                  name="resolution-status"
                  value="unresolved"
                  checked={!isResolved}
                  onChange={() => setIsResolved(false)}
                  className="sr-only"
                />
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      !isResolved ? 'border-orange-500' : 'border-ink-300'
                    }`}
                  >
                    {!isResolved && (
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-ink-900">미해결</p>
                    <p className="text-sm text-ink-500">아직 조치되지 않음</p>
                  </div>
                </div>
              </label>

              <label
                className={`flex-1 cursor-pointer rounded-card border-2 p-4 transition-colors ${
                  isResolved
                    ? 'border-green-500 bg-green-50'
                    : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                <input
                  type="radio"
                  name="resolution-status"
                  value="resolved"
                  checked={isResolved}
                  onChange={() => setIsResolved(true)}
                  className="sr-only"
                />
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isResolved ? 'border-green-500' : 'border-ink-300'
                    }`}
                  >
                    {isResolved && (
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-ink-900">해결됨</p>
                    <p className="text-sm text-ink-500">조치 완료됨</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* 사진 첨부 (다중) */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              {isResolved ? '해결 사진' : '현재 상황 사진'}{' '}
              <span className="text-ink-400">(선택)</span>
            </label>
            {!isResolved && (
              <p className="text-xs text-ink-500 mb-3">
                현재 상황을 사진으로 기록할 수 있습니다.
              </p>
            )}

            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {photos.map((photo, idx) => (
                  <div
                    key={idx}
                    className={`relative w-24 h-24 rounded-card-sm overflow-hidden border-2 cursor-pointer ${
                      idx === representativeIndex ? 'border-brand-500 ring-2 ring-brand-200' : 'border-ink-200 hover:border-ink-400'
                    }`}
                    onClick={() => setCurrentData(prev => ({ ...prev, representativeIndex: idx }))}
                  >
                    <img
                      src={photo.previewUrl}
                      alt={`사진 ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {!isLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemovePhoto(idx); }}
                        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600"
                        aria-label="사진 삭제"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    )}
                    {idx === representativeIndex && photos.length > 1 && (
                      <div className="absolute bottom-0 left-0 right-0 bg-brand-500 text-white text-[10px] text-center py-0.5">
                        대표
                      </div>
                    )}
                  </div>
                ))}

                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="w-24 h-24 rounded-card-sm border-2 border-dashed border-ink-300 flex flex-col items-center justify-center text-ink-400 hover:border-brand-500 hover:text-brand-500 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs mt-1">추가</span>
                  </button>
                )}
              </div>
            )}

            {photos.length === 0 && (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`border-2 border-dashed rounded-card p-6 text-center transition-colors cursor-pointer ${
                  isLoading
                    ? 'border-ink-200 bg-ink-50 cursor-not-allowed'
                    : 'border-ink-300 hover:border-brand-500'
                }`}
                onClick={() => !isLoading && fileInputRef.current?.click()}
              >
                <UploadIcon className="mx-auto h-10 w-10 text-ink-400" />
                <p className="mt-2 text-sm text-ink-600">
                  클릭하거나 파일을 드래그하여 업로드
                </p>
                <p className="mt-1 text-xs text-ink-500">
                  PNG, JPG, HEIC 지원 (최대 10MB, {MAX_PHOTOS}장)
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isLoading}
            />
          </div>

          {/* 해결 날짜 선택 (해결됨 상태일 때만) */}
          {isResolved && (
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                해결 날짜 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <DateInput
                  label="해결 날짜"
                  value={resolvedDate}
                  onChange={setResolvedDate}
                  min={resolvedDateMin}
                  max={resolvedDateMax}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {/* 조치 사항 / 메모 */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              {isResolved ? '조치 사항' : '메모'}{' '}
              {isResolved ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-ink-400">(선택)</span>
              )}
            </label>
            {isResolved ? (
              <ActionItemsInput
                items={currentData.actionItems}
                onChange={(items) => setCurrentData(prev => ({ ...prev, actionItems: items }))}
                disabled={isLoading}
                placeholder="조치 항목 입력..."
              />
            ) : (
              <textarea
                id="action-notes"
                value={currentData.notes}
                onChange={(e) => setCurrentData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                disabled={isLoading}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
                placeholder="미해결 사유를 입력하세요..."
              />
            )}
          </div>
        </div>

        {/* 버튼 */}
      </form>
    </Modal>
  );
}

// Icons
function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

